import React, { useCallback, useMemo } from 'react'

import { AuctionManager } from '@dewicats/idls/lib/types/auction_manager'
import { IdlAccounts } from '@coral-xyz/anchor'
import JazzIcon from '@components/JazzIcon'
import OpenLinkIcon from '@assets/openLinkIcon.svg'
import { ellipsizeAddress } from '@utils/solanaUtils'
import Image from 'next/image'
import { toNumber } from '@helium/spl-utils'
import { PublicKey } from '@solana/web3.js'
import { useConnection } from '@solana/wallet-adapter-react'
import { useMediaQuery } from '@mui/material'

const BidItem = ({
  bid,
  image,
  tokenMintDecimals,
}: {
  image: string
  bid: IdlAccounts<AuctionManager>['bidRecieptV0'] & { publicKey: PublicKey }
  tokenMintDecimals: number
}) => {
  const bidPrice = useMemo(() => {
    return toNumber(bid.amount, tokenMintDecimals)
  }, [bid, tokenMintDecimals])

  const { connection } = useConnection()

  const isMobile = useMediaQuery('(max-width: 1024px)')

  const isDevnet = useMemo(() => {
    return connection.rpcEndpoint.includes('devnet')
  }, [connection?.rpcEndpoint])

  const openExplorerLink = useCallback(() => {
    window.open(
      `https://explorer.solana.com/address/${bid.publicKey.toBase58()}${isDevnet ? '?cluster=devnet' : ''}`,
      '_blank',
    )
  }, [bid?.publicKey, isDevnet])

  return (
    <div className="flex flex-wrap gap-2 w-full bg-dark-8 p-4 rounded-md">
      <div className="flex flex-1 gap-2 items-center">
        <JazzIcon
          className="flex items-center"
          publicAddress={bid.bidder.toBase58()}
          diameter={20}
        />
        <span className="text-size-2 font-normal">{`${ellipsizeAddress(
          bid.publicKey.toBase58(),
          {
            numChars: isMobile ? 4 : 8,
          },
        )}`}</span>
      </div>
      <div className="flex gap-2 items-center">
        <Image
          src={image}
          alt="auction-token"
          width={20}
          height={20}
          data-loaded="false"
          onLoad={(event) => {
            event.currentTarget.setAttribute('data-loaded', 'true')
          }}
          className={`data-[loaded=false]:animate-pulse data-[loaded=false]:bg-dark-7 rounded-full`}
        />
        <span className="text-size-2 font-normal">{`${bidPrice.toLocaleString()}`}</span>

        <button
          onClick={openExplorerLink}
          className="hover:text-gray-1 text-gray-6"
        >
          <OpenLinkIcon
            style={{
              width: '20px',
              height: '20px',
              color: 'inherit',
            }}
          />
        </button>
      </div>
    </div>
  )
}

export default BidItem
